import * as React from 'react'
// antd's OWN DisabledContext, not a transcribed copy — see the same note in
// `./Checkbox.shadcn.tsx`.
import DisabledContext from 'antd/es/config-provider/DisabledContext'

import { Switch as SwitchPrimitive } from 'src/components/ui/switch'
import { LoadingOutlined } from 'src/icons/antd-compat'
import { cn } from 'src/lib/utils'

import type { SwitchProps } from './antd-props'

/* -------------------------------------------------------------------------
 * The SHADCN half of the `src/antd/Switch` seam. `./Switch.tsx` is the
 * dispatcher that chooses between this and antd's own Switch; nothing else
 * imports this file, and nothing reaches it until someone flips the design
 * toggle (`src/design-mode`).
 *
 * Started from the `update-design` reference branch's `src/antd/Switch.tsx`.
 * Divergences from the reference are marked `[development]`.
 *
 * The EXTERNAL API is antd's own `SwitchProps`, so the 80 `<Switch>` tags that
 * import it from `src/antd` switch with no edit — including every
 * `Form.Item valuePropName="checked"` child and the `value` / `defaultValue`
 * aliases antd added in 5.12.
 *
 * [development] `.ant-switch*` IS NOT RE-EMITTED — the batch's rule. Two rules
 * in `src/storybook/styles.tsx` reached into `.ant-switch-loading` /
 * `.ant-switch-disabled`; each grew a `[data-slot='switch']` selector beside
 * it.
 *
 * THREE THINGS ANTD DOES INSIDE THE TRACK. `src/components/ui/switch.tsx`
 * shipped its Thumb as the Root's only child; it now renders `children` beside
 * the Thumb so the label can sit IN FLOW and widen the track the way antd's
 * does (an absolutely-positioned label contributed nothing to the track's
 * width, so real labels — “Active” / “Inactive” / “Synced” — were clipped by
 * the thumb):
 *   • `checkedChildren` / `unCheckedChildren` (5 call sites) — a label inside
 *     the track, on the side the thumb is NOT on. In flow, so the track grows
 *     to fit it; `order-first` is what puts it before the thumb once checked.
 *   • `loading` (5 call sites) — a spinner ON the thumb, and the control goes
 *     non-interactive. antd merges `disabled || loading` into the button's
 *     `disabled`, and so does this.
 *   • `size="small"` (31 call sites) — the registry's `size="sm"`.
 *
 * [development] The reference put the loading glyph on the side OPPOSITE the
 * thumb (`checked ? 'left-1' : 'right-1'`), where antd puts it on the thumb
 * itself. Corrected: the glyph tracks the thumb. On a LABELLED track the
 * thumb's position is decided by flow rather than by a translation, so the
 * glyph is a zero-width flex item sitting immediately after the thumb and
 * pulled back over it by half a thumb — it follows the thumb instead of
 * re-deriving its pixel offsets.
 *
 * [development] THE GLYPH'S COLOUR IS `text-muted-foreground`, ON THE WRAPPER.
 * That is the app's one loader colour (AGENTS.md's loading-states entry), and
 * it has to sit on a parent rather than on the glyph because the icon seam
 * injects `.anticon { color: inherit }` UNLAYERED — a colour utility on the
 * glyph itself loses to it whatever the specificity says.
 * `../__tests__/spin-loader-consistency.test.tsx` holds this surface to the
 * same token as the other three.
 * ------------------------------------------------------------------------- */

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      size = 'default',
      className,
      checked: checkedProp,
      defaultChecked,
      value,
      defaultValue,
      onChange,
      onClick,
      checkedChildren,
      unCheckedChildren,
      disabled: disabledProp,
      loading,
      autoFocus,
      style,
      title,
      tabIndex,
      id,
      // antd-only props that must never reach the DOM
      prefixCls: _prefixCls,
      rootClassName: _rootClassName,
      ...rest
    },
    ref
  ) => {
    const contextDisabled = React.useContext(DisabledContext)
    const disabled = disabledProp ?? contextDisabled
    // antd 5.12's aliases: `value` for `checked`, `defaultValue` for
    // `defaultChecked`. `checked` wins where both are given, as it does there.
    const resolvedChecked = checkedProp ?? value
    const resolvedDefaultChecked = defaultChecked ?? defaultValue
    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(
      resolvedDefaultChecked ?? false
    )
    const checked = resolvedChecked ?? uncontrolledChecked
    const hasLabels = checkedChildren != null || unCheckedChildren != null
    const isSmall = size === 'small'

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      const next = !checked
      if (resolvedChecked === undefined) setUncontrolledChecked(next)
      onChange?.(next, event)
      onClick?.(next, event)
    }

    return (
      <span
        data-slot="switch-wrapper"
        className={cn(
          'relative inline-flex items-center align-middle',
          // The loading glyph inherits this; see the header.
          'text-muted-foreground'
        )}
      >
        <SwitchPrimitive
          {...rest}
          ref={ref}
          id={id}
          title={title}
          tabIndex={tabIndex}
          autoFocus={autoFocus}
          size={isSmall ? 'sm' : 'default'}
          checked={checked}
          disabled={disabled || loading}
          onClick={handleClick}
          className={cn(
            hasLabels && [
              // `w-auto!` because the registry's width is written under a
              // `data-[size=…]` variant and would otherwise out-specify a
              // plain utility — the track has to be free to grow to whatever
              // its label needs. The minimum keeps a one-word label from
              // reading narrower than an unlabelled switch.
              isSmall ? 'w-auto! min-w-10' : 'w-auto! min-w-12',
              // THE THUMB'S TRAVEL IS THUMB-RELATIVE, and that only works on
              // the registry's fixed-width track: `translate-x-[calc(100%-2px)]`
              // moves it by its OWN width, so on a track widened to hold a
              // label it stops short and the label runs under it. Flexbox
              // places it instead — the thumb and the label are both in flow,
              // the pair is flush to whichever end the state names, and the
              // translation is cancelled.
              'justify-start px-0.5 data-[state=checked]:justify-end',
              '[&_[data-slot=switch-thumb]]:translate-x-0',
              '[&_[data-slot=switch-thumb]]:shrink-0',
            ],
            className
          )}
          style={style}
        >
          {loading && hasLabels && (
            // Pulled back over the thumb by half its width and then given
            // that half back on the other side, so the slot costs the widened
            // track NOTHING — a bare negative margin here shortens the track
            // and pushes the thumb out through its own right edge
            // (browser-measured). Zero-width and centred, so the glyph lands
            // on the thumb's centre wherever flow put the thumb.
            <span
              data-slot="switch-loading-slot"
              className={cn(
                'pointer-events-none z-10 flex w-0 shrink-0 items-center justify-center overflow-visible',
                isSmall ? '-ml-1.5 mr-1.5' : '-ml-2 mr-2'
              )}
            >
              <LoadingOutlined
                data-slot="switch-loading"
                className={isSmall ? 'text-[8px]' : 'text-[10px]'}
              />
            </span>
          )}
          {hasLabels && (
            // In flow, so the track measures it. It sits on the side the thumb
            // is not on: the thumb is the Root's first child, so the label
            // only has to move ahead of it once checked.
            <span
              data-slot="switch-label"
              className={cn(
                'pointer-events-none flex select-none items-center justify-center whitespace-nowrap leading-none',
                isSmall ? 'px-1 text-[10px]' : 'px-1.5 text-[11px]',
                checked
                  ? 'order-first text-primary-foreground'
                  : 'text-muted-foreground'
              )}
            >
              {checked ? checkedChildren : unCheckedChildren}
            </span>
          )}
        </SwitchPrimitive>
        {loading && !hasLabels && (
          // On the thumb, which is inset 2px from the track's edge and is the
          // same box as the glyph. The unlabelled track is the registry's own
          // fixed-width one, so these two offsets are the thumb's two resting
          // places.
          <LoadingOutlined
            data-slot="switch-loading"
            className={cn(
              'pointer-events-none absolute',
              // Smaller than the thumb it sits on, so the thumb still reads
              // as a thumb — antd's is the same proportion.
              isSmall ? 'text-[8px]' : 'text-[10px]',
              checked ? 'right-1' : 'left-1'
            )}
          />
        )}
      </span>
    )
  }
)
Switch.displayName = 'Switch'

export { Switch }
